import axios from "axios";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  airChfarApi,
  type AirChfarExerciseQuoteResponseExerciseAvailable,
} from "@b2bportal/air-chfar-api";
import { setChfarExerciseTrackingProperties } from "../../../book/actions/actions";

class ChfarExerciseError extends Error {
  expiryDate?: string;

  constructor(message: string, expiryDate?: string) {
    super(message);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ChfarExerciseError);
    }

    this.name = "ChfarExerciseError";
    this.expiryDate = expiryDate;
  }
}

export const chfarItinerariesApi = createApi({
  reducerPath: "chfarItinerariesApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["ChfarItineraries"],
  endpoints: (builder) => ({
    getChfarItineraryById: builder.query<
      AirChfarExerciseQuoteResponseExerciseAvailable,
      { itineraryId: string; contractId: string }
    >({
      queryFn: async ({ itineraryId, contractId }) => {
        const singleItineraryRequest = {
          itineraryId,
          referenceDateTime: new Date().toISOString(),
        };
        try {
          const res = await airChfarApi(axios).apiV1ChfarAirExerciseQuotePost({
            itineraryId,
            contractId,
            singleItineraryRequest,
          });

          if (res.data.AirChfarExerciseQuoteResponse === "NotAvailable") {
            const expiryDate =
              res.data.NotAvailable === "ContractExpired"
                ? res.data.expiredAt
                : undefined;
            throw new ChfarExerciseError(res.data.NotAvailable, expiryDate);
          }

          return { data: res.data };
        } catch (error) {
          if (error instanceof ChfarExerciseError) {
            return {
              error: {
                message: error.message,
                data: error.expiryDate,
              },
            };
          }
          if (axios.isAxiosError(error)) {
            return {
              error: {
                status: error.response?.status,
                message: error.message,
              },
            };
          }

          return {
            error: { message: (error as Error).message ?? "Unknown error" },
          };
        }
      },
      providesTags: ["ChfarItineraries"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            setChfarExerciseTrackingProperties(data?.trackingProperties ?? null)
          );
        } catch (error) {
          console.error("Query failed:", error);
        }
      },
    }),
  }),
});

export const { useGetChfarItineraryByIdQuery } = chfarItinerariesApi;
export const chfarItinerariesApiReducerPath = chfarItinerariesApi.reducerPath;
export const chfarItinerariesApiReducer = chfarItinerariesApi.reducer;
export const chfarItinerariesApiMiddleware = chfarItinerariesApi.middleware;

export type ChfarItinerariesApiState = ReturnType<
  typeof chfarItinerariesApi.reducer
>;
